import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { MemberApiError, resendVerificationEmail, verifyMemberEmail } from '@/services/memberAuth';

type Status = 'loading' | 'success' | 'expired' | 'error';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || searchParams.get('Token') || '';
  const { refresh } = useMemberAuth();

  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    let alive = true;
    if (!token) {
      setStatus('error');
      setMessage('驗證連結無效，請確認信件中的連結是否完整。');
      return;
    }
    verifyMemberEmail(token)
      .then(async (data) => {
        if (!alive) return;
        setStatus('success');
        setMessage(data.AlreadyVerified ? '此帳號已完成驗證，請直接登入。' : '信箱驗證成功，歡迎加入會員！');
        await refresh();
      })
      .catch((err) => {
        if (!alive) return;
        setStatus(err instanceof MemberApiError && err.expired ? 'expired' : 'error');
        setMessage(err instanceof Error ? err.message : '驗證失敗，請稍後再試。');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleResend() {
    if (!resendEmail.trim()) {
      setResendMessage('請輸入註冊時使用的 Email。');
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      await resendVerificationEmail(resendEmail.trim());
      setResendMessage('驗證信已重新寄出，請至信箱查收。');
    } catch {
      setResendMessage('發送失敗，請稍後再試。');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-md px-6 py-16 md:py-24 text-center">
          {status === 'loading' && (
            <>
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1B3D2A]/10">
                <i className="ri-loader-4-line animate-spin text-3xl text-[#1B3D2A]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1B3D2A]">驗證中...</h1>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1B3D2A]/10">
                <i className="ri-checkbox-circle-line text-3xl text-[#1B3D2A]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1B3D2A]">驗證成功</h1>
              <p className="mt-3 text-sm text-[#5A6B5A]">{message}</p>
              <button
                type="button"
                onClick={() => navigate('/account')}
                className="mt-6 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
              >
                前往會員中心
              </button>
            </>
          )}

          {(status === 'expired' || status === 'error') && (
            <>
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#fdebea]">
                <i className="ri-error-warning-line text-3xl text-[#c3423f]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1B3D2A]">{status === 'expired' ? '驗證連結已逾期' : '驗證失敗'}</h1>
              <p className="mt-3 text-sm text-[#5A6B5A]">{message}</p>

              <div className="mt-6 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-5 text-left">
                <label htmlFor="resend-email" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">重新發送驗證信</label>
                <div className="flex gap-2">
                  <input
                    id="resend-email"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="flex-1 rounded-xl border border-[#E8E0D6] px-3.5 py-2.5 text-sm outline-none focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/15"
                  />
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="shrink-0 rounded-xl bg-[#1B3D2A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#143026] disabled:opacity-60"
                  >
                    {resending ? '寄送中' : '發送'}
                  </button>
                </div>
                {resendMessage && <p className="mt-2 text-xs text-[#1B3D2A]">{resendMessage}</p>}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
